import { useNavigate } from 'react-router-dom'
import { useDashboardStats } from './useDashboardStats'
import { useEffect } from 'react'

const Dashboard = () => {
  const navigate = useNavigate()
  const { stats, topGenreMoodCombo, loading, error } = useDashboardStats()
  const currentYear = new Date().getFullYear()

  useEffect(() => {
    if (error === 'unauthenticated') {
      navigate('/login')
    }
  }, [error, navigate])

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold font-serif mb-6">Stats</h1>
      {error ? (
        <div className="text-center text-error bg-error/10 p-4 rounded">
          Something went wrong. Give it another try.
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="card bg-base-100 shadow-xl animate-pulse"
              role="status"
            >
              <div className="card-body">
                <div className="h-6 bg-base-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-base-300 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            className="card bg-base-100 shadow-xl tooltip"
            data-tip="Everything in your library — owned, playing, finished, and waiting"
          >
            <div className="card-body">
              <h2 className="card-title">Total games</h2>
              <p className="text-4xl font-bold" data-testid="total-library">
                {stats.totalLibrary}
              </p>
              <p className="text-sm opacity-70">in your library</p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl hover:bg-base-200 cursor-pointer transition-colors tooltip"
            data-tip="Games you own but haven't finished — click to see them all"
            onClick={() =>
              navigate('/app/library', {
                state: {
                  filterStatus: ['Try Again', 'Started', 'Owned', 'Come back!'],
                },
              })
            }
          >
            <div className="card-body">
              <h2 className="card-title">Unplayed</h2>
              <p className="text-4xl font-bold" data-testid="backlog">
                {stats.backlog}
              </p>
              <p className="text-sm opacity-70">
                {((stats.backlog / stats.totalLibrary) * 100).toFixed(1)}% of your library
              </p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl hover:bg-base-200 cursor-pointer transition-colors tooltip"
            data-tip="What you're playing right now — click to see them"
            onClick={() =>
              navigate('/app/library', {
                state: { filterStatus: 'Currently Playing' },
              })
            }
          >
            <div className="card-body">
              <h2 className="card-title">On the go</h2>
              <p className="text-4xl font-bold" data-testid="currently-playing">
                {stats.currentlyPlaying}
              </p>
              <p className="text-sm opacity-70">currently playing</p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl hover:bg-base-200 cursor-pointer transition-colors tooltip"
            data-tip="Games you've finished — click to see them"
            onClick={() =>
              navigate('/app/library', {
                state: {
                  filterStatus: ['Endless', 'Done', 'Satisfied', 'DNF'],
                },
              })
            }
          >
            <div className="card-body">
              <h2 className="card-title">Finished</h2>
              <p className="text-4xl font-bold" data-testid="completed">
                {stats.completed}
              </p>
              <p className="text-sm opacity-70">
                {((stats.completed / stats.totalLibrary) * 100).toFixed(1)}% completion rate
              </p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl tooltip"
            data-tip="The genre that shows up most in your library"
          >
            <div className="card-body">
              <h2 className="card-title">Favourite genre</h2>
              <p
                className="text-4xl font-bold capitalize"
                data-testid="favorite-genre"
              >
                {stats.topGenre || '—'}
              </p>
              <p className="text-sm opacity-70">your most-played genre</p>
            </div>
          </div>

          <div
            className="card bg-base-100 shadow-xl tooltip"
            data-tip="The genre and mood combination that comes up most across your games"
          >
            <div className="card-body">
              <h2 className="card-title">Your kind of game</h2>
              <p
                className="text-2xl font-bold capitalize"
                data-testid="most-common-genre-mood-combo"
              >
                {topGenreMoodCombo
                  ? `${topGenreMoodCombo.genre} • ${topGenreMoodCombo.mood}`
                  : '—'}
              </p>
              <p className="text-sm opacity-70">genre + mood combo</p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl tooltip"
            data-tip="Based on your finished games"
          >
            <div className="card-body">
              <h2 className="card-title">Favourite platform</h2>
              <p
                className="text-4xl font-bold"
                data-testid="most-used-platform"
              >
                {stats.topPlatform || '—'}
              </p>
              <p className="text-sm opacity-70">
                {stats.topPlatform
                  ? 'from finished games'
                  : 'finish more games to see this'}
              </p>
            </div>
          </div>
          <div
            className="card bg-base-100 shadow-xl hover:bg-base-200 cursor-pointer transition-colors tooltip"
            data-tip={`Games you finished in ${currentYear} — click to see them`}
            onClick={() =>
              navigate('/app/library', {
                state: {
                  filterStatus: ['Endless', 'Done', 'Satisfied', 'DNF'],
                  filterYear: currentYear,
                },
              })
            }
          >
            <div className="card-body">
              <h2 className="card-title">Finished in {currentYear}</h2>
              <p
                className="text-4xl font-bold"
                data-testid="completed-this-year"
              >
                {stats.completedThisYear}
              </p>
              <p className="text-sm opacity-70">
                {((stats.completedThisYear / stats.totalLibrary) * 100).toFixed(1)}% of your library
              </p>
            </div>
          </div>

          <div
            className="card bg-base-100 shadow-xl tooltip"
            data-tip="The mood that shows up most across your games"
          >
            <div className="card-body">
              <h2 className="card-title">Your mood</h2>
              <p
                className="text-4xl font-bold capitalize"
                data-testid="most-common-mood"
              >
                {stats.topMood || '—'}
              </p>
              <p className="text-sm opacity-70">how your games make you feel</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
